import i18n from '@/libs/i18n'
import store from '@/store'
import useErrorManager from '@/composable/useErrorManager'
import { ref } from '@vue/composition-api'

export default function useDashboardList() {
  const articlesCategoriesReportData = ref([])
  const categoryTitles = ref([])
  const currentLanguage = ref('fa')
  const errorManager = useErrorManager()
  const managersReportData = ref([])
  const totalArticles = ref(null)
  const totalComments = ref(null)
  const totalFaq = ref(null)
  const totalManagers = ref(null)
  const totalQuestions = ref(null)
  const totalServices = ref(null)
  const totalSpecialists = ref(null)
  const totalSubscriptions = ref(null)
  const totalSuggestions = ref(null)
  const totalTestimonials = ref(null)
  const totalUsers = ref(null)
  const totalVideos = ref(null)
  const userArticlesReportData = ref([])
  const userList = ref([])

  const fetchCategories = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'articles-categories' })
      .then(response => {
        categoryTitles.value = response.data.data.map(item => item.title[lang])
      })
      .catch(err => {
        errorManager(err)
      })
  }

  const fetchTotalArticles = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang, type: 'article' }, endpoint: 'articles' })
      .then(response => {
        totalArticles.value = {
          icon: 'ListIcon',
          color: 'light-danger',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.article'),
        }
      })
  }

  const fetchTotalVideos = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang, type: 'video' }, endpoint: 'articles' })
      .then(response => {
        totalVideos.value = {
          icon: 'VideoIcon',
          color: 'light-warning',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.video'),
        }
      })
  }

  const fetchTotalQuestions = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'questions' })
      .then(response => {
        totalQuestions.value = {
          icon: 'HelpCircleIcon',
          color: 'light-dark',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.question'),
        }
      })
  }

  const fetchTotalServices = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'services' })
      .then(response => {
        totalServices.value = {
          icon: 'FileTextIcon',
          color: 'light-success',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.service'),
        }
      })
  }

  const fetchTotalUsers = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'users' })
      .then(response => {
        totalUsers.value = {
          icon: 'UserIcon',
          color: 'light-primary',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.user'),
        }
      })
  }

  const fetchTotalManagers = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'managers' })
      .then(response => {
        totalManagers.value = {
          icon: 'UserCheckIcon',
          color: 'light-info',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.managers'),
        }
      })
  }

  const fetchTotalSubscriptions = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'subscription' })
      .then(response => {
        totalSubscriptions.value = {
          icon: 'MailIcon',
          color: 'light-warning',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.subscription'),
        }
      })
  }

  const fetchTotalFaq = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'faq' })
      .then(response => {
        totalFaq.value = {
          icon: 'HelpCircleIcon',
          color: 'light-dark',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.faq'),
        }
      })
  }

  const fetchTotalSuggestions = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'suggestions' })
      .then(response => {
        totalSuggestions.value = {
          icon: 'MailIcon',
          color: 'light-warning',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.suggestion'),
        }
      })
  }

  const fetchTotalSpecialists = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'specialist' })
      .then(response => {
        totalSpecialists.value = {
          icon: 'UserPlusIcon',
          color: 'light-danger',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.specialist'),
        }
      })
  }

  const fetchTotalComments = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'comments' })
      .then(response => {
        totalComments.value = {
          icon: 'MessageSquareIcon',
          color: 'light-warning',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.comments'),
        }
      })
  }
  const fetchTotalTestimonials = (lang = 'fa') => {
    store.dispatch('crud/fetchList', { queryParams: { lang }, endpoint: 'testimonials' })
      .then(response => {
        totalTestimonials.value = {
          icon: 'HashIcon',
          color: 'light-warning',
          title: `${response.data.total}`,
          subtitle: i18n.t('general.testimonials'),
        }
      })
  }

  const fetchCategoriesReport = () => store.dispatch(
    'crud/fetchList',
    {
      queryParams: { lang: currentLanguage.value, type: 'article' },
      endpoint: 'articles-categories/content-report',
    },
  )
    .then(response => {
      const { data } = response.data
      const countPostType = []
      countPostType.categories = []
      countPostType.articles = []
      countPostType.videos = []
      countPostType.questions = []
      data.forEach(element => {
        countPostType.categories.push(element.articleCategory.title[currentLanguage.value])
        countPostType.articles.push(element.articles)
        countPostType.videos.push(element.videos)
        countPostType.questions.push(element.questions)
      })

      categoryTitles.value = countPostType.categories

      articlesCategoriesReportData.value = [{
        name: i18n.t('dashboard.articles'),
        type: 'bar',
        stack: 'advertising',
        data: countPostType.articles,
        barWidth: '30%',
      },
      {
        name: i18n.t('dashboard.videos'),
        type: 'bar',
        stack: 'advertising',
        data: countPostType.videos,
        barWidth: '30%',
      },
      {
        name: i18n.t('dashboard.questions'),
        type: 'bar',
        stack: 'advertising',
        data: countPostType.questions,
        barWidth: '30%',
      }]
    })

  const getAllStatistic = () => {
    fetchTotalArticles()
    fetchTotalComments()
    fetchTotalFaq()
    fetchTotalManagers()
    fetchTotalQuestions()
    fetchTotalServices()
    fetchTotalSpecialists()
    fetchTotalSubscriptions()
    fetchTotalSuggestions()
    fetchTotalTestimonials()
    fetchTotalUsers()
    fetchTotalVideos()
  }

  const fetchUserCategoriesReport = () => store
    .dispatch('crud/fetchList', {
      queryParams: { lang: currentLanguage.value, type: 'article' },
      endpoint: 'users/content-report',
    })
    .then(response => {
      const { data } = response.data
      const countPostType = []
      countPostType.users = []
      countPostType.articles = []
      countPostType.videos = []
      countPostType.questions = []
      data.forEach(element => {
        if (element.articles || element.videos || element.questions) {
          countPostType.users.push(`${element.user.fullName}/${element.user.phoneNumber}`)
          countPostType.articles.push(element.articles)
          countPostType.videos.push(element.videos)
          countPostType.questions.push(element.questions)
        }
      })

      userList.value = countPostType.users

      userArticlesReportData.value = [
        {
          name: i18n.t('dashboard.articles'),
          type: 'bar',
          stack: 'advertising',
          data: countPostType.articles,
          barMaxWidth: '20%',
          barMinWidth: '10px',
        },
        {
          name: i18n.t('dashboard.videos'),
          type: 'bar',
          stack: 'advertising',
          data: countPostType.videos,
          barMaxWidth: '20%',
          barMinWidth: '10px',
        },
        {
          name: i18n.t('dashboard.questions'),
          type: 'bar',
          stack: 'advertising',
          data: countPostType.questions,
          barMaxWidth: '20%',
          barMinWidth: '10px',
        },
      ]
    })

  const fetchManagersReport = () => store
    .dispatch('crud/fetchList', {
      queryParams: { lang: currentLanguage.value, type: 'manager' },
      endpoint: 'managers/content-report',
    })
    .then(response => {
      const { data } = response.data
      const countPostType = []
      countPostType.users = []
      countPostType.articles = []
      countPostType.videos = []
      data.forEach(element => {
        if (element.articles || element.videos) {
          countPostType.users.push(`${element.manager.firstName.fa}/${element.manager.lastName.fa}`)
          countPostType.articles.push(element.articles)
          countPostType.videos.push(element.videos)
        }
      })

      userList.value = countPostType.users

      managersReportData.value = [
        {
          name: i18n.t('dashboard.articles'),
          type: 'bar',
          stack: 'advertising',
          data: countPostType.articles,
          barMaxWidth: '20%',
          barMinWidth: '10px',
        },
        {
          name: i18n.t('dashboard.videos'),
          type: 'bar',
          stack: 'advertising',
          data: countPostType.videos,
          barMaxWidth: '20%',
          barMinWidth: '10px',
        },
      ]
    })

  return {
    articlesCategoriesReportData,
    categoryTitles,
    currentLanguage,
    fetchCategories,
    fetchCategoriesReport,
    fetchManagersReport,
    fetchUserCategoriesReport,
    getAllStatistic,
    managersReportData,
    totalArticles,
    totalComments,
    totalFaq,
    totalManagers,
    totalQuestions,
    totalServices,
    totalSpecialists,
    totalSubscriptions,
    totalSuggestions,
    totalTestimonials,
    totalUsers,
    totalVideos,
    userArticlesReportData,
    userList,
  }
}
